<template>
  <div class="mb40">
    <GoBack />
  </div>
  <section class="mb40">
    <company-block :company="company" />
  </section>
  <section class="mb40">
    <h2>Добавление документов</h2>
    <CStep1 v-model="DocForm_.files" />
    <CSignatureType v-if="DocForm_.files?.length" v-model="DocForm_.signatureType" />
    <CSignatore v-if="DocForm_.files?.length" v-model="DocForm_.contact" />
    <COptions
      v-if="DocForm_.files?.length"
      :form="DocForm_"
      @update="setAgreement($event)"
      @updateProp="updateForm($event)"
      ref="OptionsComponent"
    />
  </section>
  <section v-if="DocForm_.files?.length">
    <PButton title="Подписать и отправить" @click="send()" />
  </section>

  <!-- KEP modal -->
  <PopUp :modalOpen="kepModalOpen" @close="onKepCancel" ref="kepPopup">
    <h3>Подписание КЭП</h3>
    
    <!-- Dynamic content based on state -->
    <div v-if="kepModalContent && !certificates.length" v-html="kepModalContent"></div>
    
    <div v-else-if="certificates.length">
      <h6>Выберите сертификат</h6>
      <CertificateList 
        :certificates="certificates" 
        :selectedThumb="selectedCertificate"
        @select="handleCertificateSelect"
      />
    </div>
  </PopUp>

  <!-- Message popup -->
  <MessagePopup 
    v-if="showKepMessage" 
    :message="kepMessageText" 
    type="kep" 
    position="top-right" 
    :duration="3000"
    @close="showKepMessage = false"
  />
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref } from "vue";
import type { DocForm, SignedDoc, Certificate } from "@/interfaces/interface";
import { CompanyInfo } from "@/interfaces/company";
import CStep1 from "@/components/blocks/wizard/CStep1.vue";
import CSignatureType from "@/components/blocks/wizard/CSignatureType.vue";
import CSignatore from "@/components/blocks/wizard/CSignatore.vue";
import COptions from "@/components/blocks/wizard/COptions.vue";
import { state } from "@/store/state";
import PButton from "@/components/ui/PButton.vue";
import GoBack from "@/components/general/GoBack.vue";
import CompanyBlock from "@/components/blocks/CompanyBlock.vue";
import slon from "@/api/slon";
import api from "@/api/api";
import PopUp from "@/components/general/PopUp.vue";
import SignKep from "@/api/cades";
import MessagePopup from '@/components/ui/MessagePopup.vue';
import CertificateList from "@/components/blocks/CertificateList.vue";

type ParameterName = keyof DocForm;

export default defineComponent({
  name: "WizardView",
  components: {
    GoBack,
    CStep1,
    CSignatureType,
    CSignatore,
    COptions,
    PButton,
    CompanyBlock,
    PopUp,
    MessagePopup,
    //eslint-disable-next-line
    CertificateList,
  },
  setup() {
    const company = ref<CompanyInfo>();
    const selectedId = ref<number>(0);
    const OptionsComponent = ref<typeof COptions>();

    // KEP modal state
    const kepModalOpen = ref(false);
    const kepModalResolver = ref<((v: boolean) => void) | null>(null);
    const kepPopup = ref<typeof PopUp>();
    
    // Reactive content for KEP modal
    const kepModalContent = ref<string>('');

    // Message state
    const showKepMessage = ref(false);
    const kepMessageText = ref('');

    // Show message function for KEP operations
    const showMessage = (msg: string) => {
        kepMessageText.value = msg;
        showKepMessage.value = true;
    };

    // Reactive state for certificates
    const certificates = ref<Certificate[]>([]);
    const selectedCertificate = ref<string>('');
    const signKep = ref<SignKep | null>(null);

    // Create popup adapter for SignKep
    const createPopupAdapter = (onCancel: () => void) => ({
        setContent: (html: string) => {
            kepModalContent.value = html;
        },
        bindEvents: (event: string, handler: (e: Event) => void, selector: string) => {
            console.log('Binding events:', event, selector);
        },
        remove: () => {
            onCancel();
        },
        onCertificatesReady: (certs: Certificate[]) => {
            certificates.value = certs;
            // Clear the loading content since we're now showing the component
            kepModalContent.value = '';
        },
        onCertificatesUpdated: (certs: Certificate[]) => {
            certificates.value = certs;
        }
    });

    onMounted(() => {
      if (state.client) DocForm_.contact = state.client;
      if (state.companyInfo) company.value = state.companyInfo;
    });

    const DocForm_: DocForm = reactive({
      files: [],
      contact: {
        name: "",
        last_name: "",
        second_name: "",
        phone: "",
      },
      agreement: "N",
      send_late: 0,
      signatureType: "пэп",
    });

    const handleCertificateSelect = (cert: Certificate) => {
        selectedCertificate.value = cert.thumb;
        signKep.value?.selectCertificate(cert.thumb);
    };

    return { 
        company, 
        selectedId, 
        DocForm_, 
        OptionsComponent, 
        kepModalOpen, 
        kepModalResolver, 
        kepPopup,
        createPopupAdapter,
        kepModalContent,
        showKepMessage,
        kepMessageText,
        showMessage,
        certificates,
        selectedCertificate,
        handleCertificateSelect,
        signKep
    };
  },
  methods: {
    setAgreement(val: string) {
      this.DocForm_.agreement = val ? "Y" : "N";
    },
    updateForm(data: DocForm) {
      console.log(data);
      let prop: ParameterName;
      for (prop in data) {
        if (!data[prop]) continue;
        if (prop === "send_late") {
          this.DocForm_.send_late = data[prop];
        }
      }
    },
    validate(): boolean {
      let res = true;
      if (
        !this.DocForm_.contact.name.length ||
        !this.DocForm_.contact.last_name.length ||
        !this.DocForm_.contact.phone.length ||
        this.DocForm_.agreement !== "Y" ||
        !this.DocForm_.signatureType
      ) {
        res = false;
        if (this.DocForm_.agreement !== "Y") {
          this.OptionsComponent?.validateEvent(false);
        }
      }

      return res;
    },

    // Promise-based KEP modal
    waitForKepModal(): Promise<boolean> {
      this.kepModalOpen = true;
      return new Promise<boolean>((resolve) => {
        this.kepModalResolver = resolve;
      });
    },
    kepConfirm() {
      this.kepModalOpen = false;
      this.kepModalResolver?.(true);
      this.kepModalResolver = null;
    },
    onKepCancel() {
      this.kepModalOpen = false;
      this.kepModalResolver?.(false);
      this.kepModalResolver = null;
    },

    async send() {
      if (!this.validate()) return;

      // If KЭП is selected, open modal and wait for user to finish or cancel
    if (this.DocForm_.signatureType === "кэп" && this.DocForm_.files?.length) {
      // console.log(this.DocForm_.files);
        const popupAdapter = this.createPopupAdapter(this.onKepCancel);

        this.showMessage('Проверяем доступность плагина');
        //eslint-disable-next-line
        set_load_timeout(3000)
        //eslint-disable-next-line
        await cadesplugin.then(()=>{
          // Create SignKep instance with showMessage function
          this.signKep = new SignKep(
            this.DocForm_.files || [], 
              popupAdapter, 
              (signedDocs: SignedDoc[]) => {
                console.log({signedDocs});
                
                  this.DocForm_.fileSigs = signedDocs.map(item => item.sigFile);
                  this.DocForm_.files = signedDocs.map(item => item.doc);
                  this.kepConfirm();
              },
              this.showMessage // Pass the Vue component's showMessage function
          );
          
          this.signKep.run();  
        })
        
        // Wait for user to complete signing
        const proceed = await this.waitForKepModal();
        if (!proceed) return;
    }

      const data = new FormData();

      data.append("name", this.DocForm_.contact.name);
      data.append("last_name", this.DocForm_.contact.last_name);
      data.append("second_name", this.DocForm_.contact.second_name ?? "");
      data.append("phone", this.DocForm_.contact.phone);
      data.append("agreement", this.DocForm_.agreement);
      data.append("signatureType", this.DocForm_.signatureType);

      // const sigList: (File|Blob)[] = [];

      // If KЭП: ensure .sig present for each file and collect them in order
      // if (this.DocForm_.signatureType === "кэп") {
      //   const sigMap = new Map<string, File|Blob>();
      //   for (const sig of this.DocForm_.fileSigs ?? []) {
      //     // Prefer File to check names; Blob without name cannot be matched
      //     if (sig instanceof File) sigMap.set(sig.name, sig);
      //   }
      //   if (!this.DocForm_.files?.length) {
      //     alert("Нет файлов для отправки");
      //     return;
      //   }
      //   for (const f of this.DocForm_.files) {
      //     if (f instanceof Blob) {
      //       // Files selected from computer (File extends Blob)
      //       if (!(f instanceof File)) {
      //         alert("Невозможно сопоставить подпись: отсутствует имя файла");
      //         return;
      //       }
      //       const expected = `${f.name}.sig`;
      //       const sig = sigMap.get(expected);
      //       if (!sig) {
      //         alert(`Для файла "${f.name}" не найден файл подписи "${expected}"`);
      //         return;
      //       }
      //       sigList.push(sig);
      //     } else {
      //       // ClientDoc: use provided name to match signature
      //       const expected = `${f.name}.sig`;
      //       const sig = sigMap.get(expected);
      //       if (!sig) {
      //         alert(`Для файла "${f.name}" не найден файл подписи "${expected}"`);
      //         return;
      //       }
      //       sigList.push(sig);
      //     }
      //   }
      // }

      if (this.DocForm_.files?.length) {
        //let sigIndex = 0;

        for (const file of this.DocForm_.files) {
          console.log({file});
          if (file instanceof Blob || file instanceof File) data.append("file[]", file);
          else {
            if(!file.full_link) continue;
            const fileData = await api.getDocByURLFetch(
              file.full_link,
              file.name
            );

            data.append("file[]", fileData.file);
            data.append("fileName[]", file.name);
          }
        }

        // Append signatures for KЭП (aligned by order with file[])
        if (this.DocForm_.signatureType === "кэп") {
          for (const sig of this.DocForm_?.fileSigs ?? []) {
            data.append("sigFile[]", sig);
          }
        }
      }
      const res = await slon.addDocument(data);
      if (res.status) {
        state.setRouteFromPath("/");
      } else {
        alert("Документ не был отправлен");
      }
    },
  },
});
</script>

<style scoped>
.mb40 {
  margin-bottom: 40px;
}
h2 {
  margin-bottom: 20px;
}
</style>